import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { m5Api, type AdminRow } from "../../../api/m5";
import { PageHeader } from "../../../components/ComingSoon";
import { Modal } from "../../../components/Overlay";
import { Button, StatusPill, TextInput } from "../../../components/ui";
import { useAuth } from "../../../context/AuthContext";
import { LoadState, useLoad } from "../shared";

export default function AdminAdministrators() {
  const { session } = useAuth();
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => m5Api.admins(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const [form, setForm] = useState({ name: "", email: "" });
  const [temp, setTemp] = useState<{ email: string; pw: string } | null>(null);
  const [target, setTarget] = useState<AdminRow | null>(null);
  const [reason, setReason] = useState("");
  const create = async () => {
    try { const r = await m5Api.createAdmin(form.name, form.email); setTemp({ email: r.email_normalized, pw: r.temporary_password }); setForm({ name: "", email: "" }); setNonce((n) => n + 1); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const toggle = async () => {
    if (!target) return;
    try { await m5Api.setAdmin(target.id, target.status !== "ACTIVE", reason); toast.success("Saved"); setTarget(null); setReason(""); setNonce((n) => n + 1); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid="admin-administrators-page" className="animate-rise space-y-8">
      <PageHeader title="Administrators" />
      <p className="max-w-3xl text-sm text-zinc-400">New administrators get a one-time temporary password, must choose their own password and enroll two-factor authentication at first sign-in. Payroll approval always needs two different administrators.</p>
      <div className="flex flex-wrap gap-2" data-testid="admin-create-form">
        <TextInput className="w-56" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-create-name" />
        <TextInput className="w-72" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-email" />
        <Button onClick={create} disabled={!form.name.trim() || !form.email.includes("@")} data-testid="admin-create-btn">Create administrator</Button>
      </div>
      <LoadState error={error} loading={!data && !error} testId="admins-load" />
      <table className="w-full text-left text-sm text-zinc-300" data-testid="admins-table">
        <thead><tr className="label-caps text-zinc-500">{["Name", "Email", "Status", "2FA", "Last sign-in", ""].map((h) => <th key={h} className="py-2 font-normal">{h}</th>)}</tr></thead>
        <tbody>
          {data?.map((a) => (
            <tr key={a.id} className="border-t border-white/5" data-testid={`admin-row-${a.id}`}>
              <td className="py-2 text-white">{a.name}{a.is_demo && <span className="ml-2 text-xs text-amber-300">DEMO</span>}{a.id === session?.admin?.id && <span className="ml-2 text-xs text-zinc-500">(you)</span>}</td>
              <td>{a.email_normalized}</td>
              <td><StatusPill status={a.status} /></td>
              <td>{a.must_change_password ? "Password change pending" : a.mfa_enabled ? "Enrolled" : "Not enrolled"}</td>
              <td className="font-mono text-xs">{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : "—"}</td>
              <td>{a.id !== session?.admin?.id && <button className="text-xs font-semibold text-signal hover:text-white" onClick={() => setTarget(a)} data-testid={`admin-toggle-${a.id}`}>{a.status === "ACTIVE" ? "Deactivate" : "Reactivate"}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal open={!!temp} title="Temporary password (shown once)" onClose={() => setTemp(null)} testId="admin-temp-modal">
        <p className="text-sm text-zinc-300">Give this to <strong>{temp?.email}</strong> through a safe channel. It won't be shown again.</p>
        <p className="mt-3 select-all rounded-sm bg-ink p-3 font-mono text-lg text-white" data-testid="admin-temp-password">{temp?.pw}</p>
      </Modal>
      <Modal open={!!target} title={target?.status === "ACTIVE" ? `Deactivate ${target?.name}` : `Reactivate ${target?.name}`} onClose={() => setTarget(null)} testId="admin-toggle-modal">
        {target?.status === "ACTIVE" && <p className="mb-3 text-sm text-amber-200">Their sessions end immediately.</p>}
        <TextInput placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-toggle-reason" />
        <Button className="mt-3" onClick={toggle} disabled={!reason.trim()} data-testid="admin-toggle-confirm">Confirm</Button>
      </Modal>
    </div>
  );
}
